// src/pages/seller/store/SellerStorePage.tsx
import React, { useState, useEffect } from 'react';
import { Star, Package, ShoppingCart, MapPin, Clock, CheckCircle, ArrowLeft, Share2, Heart } from 'lucide-react';
import { sellersApi } from '@/services/api';
import { useParams, useNavigate } from 'react-router-dom';
import Button from '@/components/ui/Button';
//import ProductCard from '@/components/product/ProductCard';
import ProductCardSimple from '@/components/product/ProductCardSimple';
import Rating from '@/components/ui/Rating';
import Tab from '@/components/ui/Tab';
import Skeleton from '@/components/product/ProductCardSkeleton';
import { ProductsResponse } from '@/services/api/sellers';

const SellerStorePage: React.FC = () => {
    const { sellerId } = useParams<{ sellerId: string }>();
    const navigate = useNavigate();
    const [store, setStore] = useState<any>(null);
    //const [products, setProducts] = useState<any[]>([]);
    //const [pagination, setPagination] = useState<ProductsResponse["pagination"] | null>(null);
    const [productsResponse, setProductsResponse] = useState<ProductsResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState('products');
    const [currentPage, setCurrentPage] = useState(1);
    const productsPerPage = 8;

    useEffect(() => {
        const fetchStoreData = async () => {
            setLoading(true);
            setError(null);
            try {
                // Obtener información del vendedor
                const storeData = await sellersApi.getPublicSellerInfo(Number(sellerId));
                setStore(storeData);

                // Obtener productos del vendedor
                const productsData = await sellersApi.getSellerProducts(Number(sellerId), {
                    page: currentPage,
                    per_page: productsPerPage,
                    status: 'active'
                });
                console.log('Products data:', productsData);
                //setProducts(productsData.data);
                //setPagination(productsData.pagination);
                setProductsResponse(productsData);

            } catch (err) {
                console.error('Error loading store data:', err);
                setError('No se pudo cargar la información de la tienda');
            } finally {
                setLoading(false);
            }
        };

        fetchStoreData();
    }, [sellerId, currentPage]);

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    <div className="mb-6">
                        <Skeleton className="h-8 w-32" />
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        <div className="lg:col-span-1">
                            <Skeleton className="h-64 rounded-lg" />
                        </div>
                        <div className="lg:col-span-2">
                            <Skeleton className="h-96 rounded-lg" />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center p-6 max-w-md">
                    <div className="mx-auto h-12 w-12 text-red-500 mb-4">
                        <CheckCircle className="w-full h-full" />
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Error al cargar la tienda</h3>
                    <p className="text-gray-600 mb-6">{error}</p>
                    <Button variant="primary" onClick={() => window.location.reload()}>
                        Reintentar
                    </Button>
                    <Button variant="secondary" onClick={() => navigate('/')} className="ml-3">
                        Volver al inicio
                    </Button>
                </div>
            </div>
        );
    }

    if (!store) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center p-6 max-w-md">
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Tienda no encontrada</h3>
                    <p className="text-gray-600 mb-6">La tienda que buscas no existe o ha sido eliminada</p>
                    <Button variant="primary" onClick={() => navigate('/')}>
                        Volver al inicio
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Banner de la tienda */}
            <div className="relative h-64 bg-gray-200 overflow-hidden">
                {store.store_banner ? (
                    <img
                        src={store.store_banner}
                        alt={`Banner de ${store.store_name}`}
                        className="w-full h-full object-cover"
                    />
                ) : (
                    <div className="w-full h-full bg-gradient-to-r from-blue-500 to-purple-600"></div>
                )}

                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent h-32">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-end pb-6">
                        <div className="flex items-end">
                            <div className="mr-4 -mb-8">
                                <div className="w-20 h-20 rounded-full border-4 border-white bg-white overflow-hidden">
                                    {store.store_logo ? (
                                        <img
                                            src={store.store_logo}
                                            alt={`Logo de ${store.store_name}`}
                                            className="w-full h-full object-cover"
                                        />
                                    ) : (
                                        <div className="w-full h-full bg-gray-300 flex items-center justify-center">
                                            <span className="text-2xl font-bold text-gray-500">
                                                {store.store_name.charAt(0).toUpperCase()}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div>
                                <h1 className="text-2xl font-bold text-white">{store.store_name}</h1>
                                <div className="flex items-center mt-1">
                                    <Rating value={store.rating_average || 0} size="sm" />
                                    <span className="ml-2 text-sm text-white/90">
                                        ({store.rating_count || 0} reseñas)
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="absolute top-4 left-4">
                    <Button
                        variant="secondary"
                        size="sm"
                        leftIcon={<ArrowLeft className="w-4 h-4" />}
                        onClick={() => navigate(-1)}
                    >
                        Volver
                    </Button>
                </div>

                <div className="absolute top-4 right-4 flex space-x-2">
                    <Button
                        variant="secondary"
                        size="sm"
                        leftIcon={<Share2 className="w-4 h-4" />}
                    >
                        Compartir
                    </Button>
                    <Button
                        variant="secondary"
                        size="sm"
                        leftIcon={<Heart className="w-4 h-4" />}
                    >
                        Guardar
                    </Button>
                </div>
            </div>

            {/* Contenido principal */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
                    {/* Sidebar */}
                    <div className="lg:col-span-1">
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                            <h2 className="text-lg font-semibold text-gray-900 mb-4">Información de la tienda</h2>

                            {store.store_description && (
                                <div className="mb-4">
                                    <p className="text-sm text-gray-600">{store.store_description}</p>
                                </div>
                            )}

                            <div className="space-y-4">
                                <div className="flex items-start">
                                    <MapPin className="w-5 h-5 text-gray-500 mt-0.5 mr-2 flex-shrink-0" />
                                    <div>
                                        <h3 className="text-sm font-medium text-gray-900">Ubicación</h3>
                                        <p className="text-sm text-gray-600">
                                            {store.business_address || 'No especificada'}
                                        </p>
                                    </div>
                                </div>

                                <div className="flex items-start">
                                    <Clock className="w-5 h-5 text-gray-500 mt-0.5 mr-2 flex-shrink-0" />
                                    <div>
                                        <h3 className="text-sm font-medium text-gray-900">Tiempo de entrega</h3>
                                        <p className="text-sm text-gray-600">2-5 días hábiles</p>
                                    </div>
                                </div>

                                <div className="flex items-start">
                                    <CheckCircle className="w-5 h-5 text-gray-500 mt-0.5 mr-2 flex-shrink-0" />
                                    <div>
                                        <h3 className="text-sm font-medium text-gray-900">Estado de verificación</h3>
                                        <p className="text-sm text-gray-600 capitalize">
                                            {store.verification_status === 'verified' ? (
                                                <span className="text-green-600">Verificado</span>
                                            ) : (
                                                'No verificado'
                                            )}
                                        </p>
                                    </div>
                                </div>

                                <div className="flex items-start">
                                    <ShoppingCart className="w-5 h-5 text-gray-500 mt-0.5 mr-2 flex-shrink-0" />
                                    <div>
                                        <h3 className="text-sm font-medium text-gray-900">Ventas totales</h3>
                                        <p className="text-sm text-gray-600">{store.total_sales || 0}</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mt-4">
                            <h2 className="text-lg font-semibold text-gray-900 mb-4">Calificaciones</h2>
                            <div className="flex items-center justify-center mb-4">
                                <div className="text-center">
                                    <div className="text-4xl font-bold text-gray-900">
                                        {store.rating_average ? store.rating_average.toFixed(1) : '0.0'}
                                    </div>
                                    <Rating value={store.rating_average || 0} className="mt-2" />
                                    <div className="text-sm text-gray-500 mt-1">
                                        {store.rating_count || 0} reseñas
                                    </div>
                                </div>
                            </div>
                            <Button variant="primary" className="w-full">
                                Ver todas las reseñas
                            </Button>
                        </div>
                    </div>

                    {/* Contenido principal */}
                    <div className="lg:col-span-3">
                        {/* Pestañas */}
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mb-6">
                            <div className="border-b border-gray-200">
                                <nav className="flex -mb-px">
                                    <Tab
                                        active={activeTab === 'products'}
                                        onClick={() => setActiveTab('products')}
                                        icon={<Package className="w-4 h-4 mr-2" />}
                                    >
                                        Productos
                                    </Tab>
                                    <Tab
                                        active={activeTab === 'reviews'}
                                        onClick={() => setActiveTab('reviews')}
                                        icon={<Star className="w-4 h-4 mr-2" />}
                                    >
                                        Reseñas
                                    </Tab>
                                    <Tab
                                        active={activeTab === 'about'}
                                        onClick={() => setActiveTab('about')}
                                        icon={<MapPin className="w-4 h-4 mr-2" />}
                                    >
                                        Sobre la tienda
                                    </Tab>
                                </nav>
                            </div>
                        </div>

                        {/* Contenido de las pestañas */}
                        {activeTab === 'products' && (
                            <div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                                    {productsResponse && productsResponse?.data?.length > 0 ? (
                                        productsResponse.data?.map((product: any) => (
                                            <ProductCardSimple
                                                key={product.id}
                                                product={product}
                                            //showSeller={false}
                                            />
                                        ))
                                    ) : (
                                        <div className="col-span-full py-12 text-center">
                                            <Package className="mx-auto h-12 w-12 text-gray-400" />
                                            <h3 className="mt-2 text-sm font-medium text-gray-900">
                                                No hay productos disponibles
                                            </h3>
                                            <p className="mt-1 text-sm text-gray-500">
                                                Esta tienda no tiene productos publicados actualmente.
                                            </p>
                                        </div>
                                    )}
                                </div>

                                {/* Paginación */}
                                {productsResponse?.pagination && productsResponse.pagination.total > 0 && (
                                    <div className="mt-8">
                                        {/* Información de paginación */}
                                        <div className="text-center mb-4 text-sm text-gray-600">
                                            Página {productsResponse.pagination.current_page} de {productsResponse.pagination.total_pages}
                                            ({productsResponse.pagination.total} productos en total)
                                        </div>

                                        {/* Botones de navegación */}
                                        <div className="flex justify-center">
                                            <Button
                                                variant="secondary"
                                                disabled={productsResponse.pagination.current_page === 1}
                                                onClick={() => setCurrentPage(prev => prev - 1)}
                                                leftIcon={<ArrowLeft className="w-4 h-4" />}
                                                className="mr-2"
                                            >
                                                Anterior
                                            </Button>
                                            <Button
                                                variant="secondary"
                                                disabled={!productsResponse.pagination.has_more}
                                                onClick={() => setCurrentPage(prev => prev + 1)}
                                                rightIcon={<ArrowLeft className="w-4 h-4 transform rotate-180" />}
                                            >
                                                Siguiente
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {activeTab === 'reviews' && (
                            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                                <h3 className="text-lg font-medium text-gray-900 mb-4">Reseñas de la tienda</h3>
                                <div className="text-center py-12">
                                    <Star className="mx-auto h-12 w-12 text-gray-400" />
                                    <h3 className="mt-2 text-sm font-medium text-gray-900">
                                        No hay reseñas todavía
                                    </h3>
                                    <p className="mt-1 text-sm text-gray-500">
                                        Sé el primero en dejar una reseña para esta tienda.
                                    </p>
                                    <div className="mt-6">
                                        <Button variant="primary">
                                            Escribir una reseña
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeTab === 'about' && (
                            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                                <h3 className="text-lg font-medium text-gray-900 mb-4">Sobre {store.store_name}</h3>
                                {store.store_description ? (
                                    <div className="prose prose-sm max-w-none text-gray-600">
                                        {store.store_description}
                                    </div>
                                ) : (
                                    <p className="text-gray-500">Esta tienda no ha proporcionado información adicional.</p>
                                )}

                                <div className="mt-8">
                                    <h4 className="text-sm font-medium text-gray-900 mb-2">Información de contacto</h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <h5 className="text-xs font-medium text-gray-500 uppercase">Dirección</h5>
                                            <p className="text-sm text-gray-600 mt-1">
                                                {store.business_address || 'No especificada'}
                                            </p>
                                        </div>
                                        <div>
                                            <h5 className="text-xs font-medium text-gray-500 uppercase">Identificación fiscal</h5>
                                            <p className="text-sm text-gray-600 mt-1">
                                                {store.tax_id || 'No especificada'}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SellerStorePage;